import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DeckAdminService, DeckSlide, PageSpec } from '../../deck-admin.service';

export type PanelMode = 'create' | 'editMeta' | 'replaceHtml';

/** One selected file plus the metadata the AI needs for it. */
interface PageRow {
  file: File | null;
  page_key: string;
  title: string;
  when: string;
  points: string;
  sizeKb: number;
}

/** `01_intro.html` → `01_intro`; `My Slide.HTML` → `my_slide`. */
function keyFromFileName(name: string): string {
  return name
    .replace(/\.html?$/i, '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}

/** `01_intro` → `01 Intro` — a starting point the uploader will usually rewrite. */
function titleFromKey(key: string): string {
  return key
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * `privacy-policy (2).html` → `privacy-policy-2.html`.
 *
 * The stored file name becomes a public URL segment (`/api/assets/<file>`), so the
 * backend allows only letters, digits, `. _ -` — the spaces and parentheses Windows
 * puts in duplicate downloads fail its validator. Sanitized here so the uploader
 * never sees that 400. Capped short enough that the backend's `<groupKey>__` prefix
 * keeps the stored name within its 128-char limit.
 */
function fileNameForUpload(name: string): string {
  const base = name
    .replace(/\.html?$/i, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-._]+|[-._]+$/g, '')
    .slice(0, 55);
  return `${base || 'slide'}.html`;
}

/**
 * Add pages to a category, edit one page's metadata, or replace one page's HTML.
 *
 * In `create` mode this takes MANY files at once: pick N .html files, get N rows with
 * `page_key` and `title` prefilled from each filename, type the narration points, drag to
 * order, submit once. The backend writes all N in one transaction — a duplicate key on
 * the 4th file rolls back the first three, so a rejected upload never leaves a
 * half-built category behind.
 *
 * Each page also takes an optional `when_to_show` — its OWN retrieval line. The
 * CATEGORY still decides when its section opens; the page-level line is what the
 * slide index embeds so a question lands on THIS page rather than a near-tied
 * sibling (without one, every page of a category embeds the same category line and
 * "picture of terminal" served the Business Panel page — call 4803).
 */
@Component({
  selector: 'app-slide-upload-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="fixed inset-0 z-[8000] bg-black/50 backdrop-blur-sm" (click)="onClose()"></div>

    <div
      class="fixed top-0 right-0 z-[8001] h-dvh w-full max-w-[620px] bg-white dark:bg-surface-1 shadow-2xl flex flex-col"
      (click)="$event.stopPropagation()"
      id="slide_upload_panel"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-5 border-b border-slate-100 dark:border-slate-700/60 shrink-0">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-sm">
            <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path *ngIf="mode === 'create'" d="M12 5v14M5 12h14"></path>
              <path *ngIf="mode !== 'create'" d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path *ngIf="mode !== 'create'" d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </div>
          <div>
            <h2 class="text-sm font-semibold text-slate-800 dark:text-white">{{ panelTitle }}</h2>
            <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{{ panelSubtitle }}</p>
          </div>
        </div>
        <button
          (click)="onClose()"
          class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors"
          id="slide_panel_close_btn"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <div
          *ngIf="errorMessage()"
          class="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-xs text-red-700 dark:text-red-400"
          id="slide_panel_error"
        >
          {{ errorMessage() }}
          <p *ngIf="showOverwriteHint()" class="mt-2 text-[11px]">
            Nothing was written. Tick <strong>Replace existing pages</strong> below to overwrite them on purpose.
          </p>
        </div>

        <div
          *ngIf="warnings().length"
          class="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 text-xs text-amber-700 dark:text-amber-400"
        >
          <p *ngFor="let w of warnings()">{{ w }}</p>
        </div>

        <!-- ── create: multi-file batch ─────────────────────────────────── -->
        <ng-container *ngIf="mode === 'create'">
          <label
            class="flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed
                   border-slate-200 dark:border-slate-700 hover:border-purple-400 dark:hover:border-line-strong
                   cursor-pointer transition-colors"
          >
            <svg class="w-6 h-6 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            <span class="text-xs font-semibold text-slate-600 dark:text-slate-300">
              Choose one or more .html slides
            </span>
            <span class="text-[10px] text-slate-400">They are added to “{{ groupTitle || groupKey }}” in the order shown below.</span>
            <input type="file" accept=".html,text/html" multiple hidden
                   (change)="onFilesSelected($event)" id="slide_panel_files" />
          </label>

          <p *ngIf="!rows.length" class="text-center text-xs text-slate-400 py-6">
            No files selected yet.
          </p>

          <div *ngFor="let row of rows; let i = index"
               class="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3"
               [attr.id]="'slide_row_' + i">
            <!-- row header: order + filename + reorder/remove -->
            <div class="flex items-center gap-2">
              <span class="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-slate-100 dark:bg-slate-700/60 text-[11px] font-bold text-slate-600 dark:text-slate-300 shrink-0">
                {{ i + 1 }}
              </span>
              <span class="flex-1 text-xs font-mono text-slate-500 dark:text-slate-400 truncate">
                {{ row.file?.name }}
                <span class="text-[10px] text-slate-300 dark:text-slate-600">({{ row.sizeKb }} KB)</span>
              </span>
              <button (click)="move(i, -1)" [disabled]="i === 0" title="Move up"
                      class="w-6 h-6 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-white/10 disabled:opacity-30 transition-colors">
                <svg class="w-3.5 h-3.5 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
              <button (click)="move(i, 1)" [disabled]="i === rows.length - 1" title="Move down"
                      class="w-6 h-6 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-white/10 disabled:opacity-30 transition-colors">
                <svg class="w-3.5 h-3.5 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <button (click)="removeRow(i)" title="Remove"
                      class="w-6 h-6 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors">
                <svg class="w-3.5 h-3.5 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <input type="text" [(ngModel)]="row.page_key" placeholder="page key"
                     class="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                            text-xs font-mono text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                     [attr.id]="'slide_row_key_' + i" />
              <input type="text" [(ngModel)]="row.title" placeholder="Title"
                     class="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                            text-xs text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                     [attr.id]="'slide_row_title_' + i" />
            </div>

            <textarea [(ngModel)]="row.when" rows="2"
                      placeholder="When to show — the questions/moments that should open THIS slide (e.g. “they ask to see the physical device, what it looks like”). Optional but strongly recommended: without it the AI can pick a sibling slide."
                      class="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                             text-xs text-slate-800 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600
                             focus:outline-none focus:ring-2 focus:ring-purple-500/40 resize-none"
                      [attr.id]="'slide_row_when_' + i"></textarea>

            <textarea [(ngModel)]="row.points" rows="3"
                      placeholder="Narration points — one per line. This is the ONLY text the AI speaks from."
                      class="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                             text-xs text-slate-800 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600
                             focus:outline-none focus:ring-2 focus:ring-purple-500/40 resize-none"
                      [attr.id]="'slide_row_points_' + i"></textarea>
          </div>

          <label
            *ngIf="rows.length"
            class="flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer
                   hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
          >
            <input type="checkbox" [(ngModel)]="overwrite" class="mt-0.5 w-4 h-4 rounded accent-amber-600"
                   id="slide_panel_overwrite" />
            <span>
              <span class="block text-xs font-semibold text-slate-700 dark:text-slate-200">Replace existing pages</span>
              <span class="block text-[10px] text-slate-400 mt-0.5">
                Off by default. A page key that already exists is rejected rather than overwritten —
                that is what stops an upload destroying a slide.
              </span>
            </span>
          </label>
        </ng-container>

        <!-- ── editMeta / replaceHtml: one page ─────────────────────────── -->
        <ng-container *ngIf="mode !== 'create'">
          <div>
            <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">Page key</label>
            <input type="text" [value]="single.page_key" disabled
                   class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50
                          text-sm font-mono text-slate-500 dark:text-slate-400 opacity-70"
                   id="slide_panel_single_key" />
          </div>

          <div *ngIf="mode === 'replaceHtml'">
            <label
              class="flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed
                     border-slate-200 dark:border-slate-700 hover:border-amber-400 cursor-pointer transition-colors"
            >
              <span class="text-xs font-semibold text-slate-600 dark:text-slate-300">
                {{ single.file ? single.file.name : 'Choose the replacement .html' }}
              </span>
              <span class="text-[10px] text-slate-400">The page's version bumps; its key and URL stay the same.</span>
              <input type="file" accept=".html,text/html" hidden (change)="onSingleFileSelected($event)"
                     id="slide_panel_single_file" />
            </label>
          </div>

          <ng-container *ngIf="mode === 'editMeta'">
            <div>
              <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
                Title <span class="text-red-500">*</span>
              </label>
              <input type="text" [(ngModel)]="single.title"
                     class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                            text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                     id="slide_panel_single_title" />
            </div>

            <div>
              <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
                When to show
              </label>
              <textarea [(ngModel)]="single.when" rows="3"
                        placeholder="The questions/moments that should open THIS slide (e.g. “they ask to see the physical device, what it looks like”). Leave empty to fall back to the category's line."
                        class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                               text-sm text-slate-800 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600
                               focus:outline-none focus:ring-2 focus:ring-purple-500/40 resize-none"
                        id="slide_panel_single_when"></textarea>
              <p class="text-[10px] text-slate-400 mt-1">
                This is how the AI picks the right slide for a question. Without it, every slide in the
                category matches the same way and a question can land on a sibling slide.
              </p>
            </div>

            <div>
              <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
                Narration points <span class="text-red-500">*</span>
              </label>
              <textarea [(ngModel)]="single.points" rows="6"
                        class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                               text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/40 resize-none"
                        id="slide_panel_single_points"></textarea>
              <p class="text-[10px] text-slate-400 mt-1">
                One per line. The only text the AI speaks from — it never reads the HTML.
              </p>
            </div>

            <div>
              <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
                Position in category
              </label>
              <input type="number" min="1" [(ngModel)]="single.position"
                     class="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50
                            text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                     id="slide_panel_single_position" />
            </div>
          </ng-container>
        </ng-container>
      </div>

      <!-- Footer -->
      <div class="flex gap-3 px-6 py-4 border-t border-slate-100 dark:border-slate-700/60 shrink-0">
        <button
          (click)="onClose()"
          class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
          id="slide_panel_cancel_btn"
        >
          Cancel
        </button>
        <button
          (click)="onSubmit()"
          [disabled]="submitting() || (mode === 'create' && !rows.length)"
          class="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-purple-500 to-indigo-600
                 hover:from-purple-600 hover:to-indigo-700 disabled:opacity-60 transition-all active:scale-95"
          id="slide_panel_submit_btn"
        >
          <span *ngIf="!submitting()">{{ submitLabel }}</span>
          <span *ngIf="submitting()" class="flex items-center justify-center gap-2">
            <span class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
            Saving…
          </span>
        </button>
      </div>
    </div>
  `,
})
export class SlideUploadPanelComponent implements OnChanges {
  @Input() mode: PanelMode = 'create';
  @Input() groupKey = '';
  @Input() groupTitle = '';
  @Input() slide: DeckSlide | null = null;
  /** Where new pages start counting from (parent passes existing page count). */
  @Input() startPosition = 1;

  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private deckService = inject(DeckAdminService);

  submitting = signal(false);
  warnings = signal<string[]>([]);
  errorMessage = signal('');
  showOverwriteHint = signal(false);

  rows: PageRow[] = [];
  overwrite = false;

  single = {
    page_key: '',
    title: '',
    when: '',
    points: '',
    position: 1,
    file: null as File | null,
  };

  get panelTitle(): string {
    if (this.mode === 'create') return 'Add Slides';
    if (this.mode === 'replaceHtml') return 'Replace HTML';
    return 'Edit Slide';
  }

  get panelSubtitle(): string {
    if (this.mode === 'create') return `Upload one or many HTML slides into this category`;
    if (this.mode === 'replaceHtml') return 'Update the HTML content (version will bump)';
    return 'Update metadata — HTML unchanged';
  }

  get submitLabel(): string {
    if (this.mode === 'create') {
      return this.rows.length === 1 ? 'Create 1 slide' : `Create ${this.rows.length} slides`;
    }
    if (this.mode === 'replaceHtml') return 'Replace HTML';
    return 'Save Changes';
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.warnings.set([]);
    this.errorMessage.set('');
    this.showOverwriteHint.set(false);

    if (changes['mode'] && this.mode === 'create') {
      this.rows = [];
      this.overwrite = false;
    }
    if ((changes['slide'] || changes['mode']) && this.slide && this.mode !== 'create') {
      this.single = {
        page_key: this.slide.key ?? '',
        title: this.slide.title ?? '',
        when: this.slide.whenToShow ?? '',
        points: (this.slide.points ?? []).join('\n'),
        position: this.slide.position ?? 1,
        file: null,
      };
    }
  }

  // ── create mode ──────────────────────────────────────────────────────────

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    for (const file of files) {
      const key = keyFromFileName(file.name);
      this.rows.push({
        file,
        page_key: key,
        title: titleFromKey(key),
        when: '',
        points: '',
        sizeKb: Math.round(file.size / 1024),
      });
    }
    input.value = ''; // let the same file be re-picked after a remove
  }

  move(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= this.rows.length) return;
    [this.rows[index], this.rows[target]] = [this.rows[target], this.rows[index]];
  }

  removeRow(index: number): void {
    this.rows.splice(index, 1);
  }

  private toPoints(text: string): string[] {
    return text
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
  }

  // ── submit ───────────────────────────────────────────────────────────────

  onSubmit(): void {
    this.errorMessage.set('');
    this.warnings.set([]);
    this.showOverwriteHint.set(false);
    if (this.mode === 'create') return this.submitBatch();
    if (this.mode === 'editMeta') return this.submitPatch();
    return this.submitReplaceHtml();
  }

  private submitBatch(): void {
    if (!this.rows.length) return this.errorMessage.set('Choose at least one HTML file.');

    for (const [i, row] of this.rows.entries()) {
      if (!row.page_key.trim()) return this.errorMessage.set(`Slide ${i + 1}: page key is required.`);
      if (!row.title.trim()) return this.errorMessage.set(`Slide ${i + 1}: title is required.`);
      if (!this.toPoints(row.points).length) {
        return this.errorMessage.set(`Slide ${i + 1}: at least one narration point is required.`);
      }
    }
    // Caught here rather than by the backend's 409, so the uploader sees which two rows
    // collide instead of a message naming only one key.
    const keys = this.rows.map((r) => r.page_key.trim());
    const dupe = keys.find((k, i) => keys.indexOf(k) !== i);
    if (dupe) return this.errorMessage.set(`Two slides share the page key “${dupe}”.`);

    const specs: PageSpec[] = this.rows.map((row, i) => ({
      page_key: row.page_key.trim(),
      title: row.title.trim(),
      narration_points: this.toPoints(row.points),
      position: this.startPosition + i,
      file_name: fileNameForUpload(row.file!.name),
      is_active: true,
      // Absent (not '') when blank: an overwrite that omits it keeps the stored line.
      ...(row.when.trim() ? { when_to_show: row.when.trim() } : {}),
    }));
    const files = this.rows.map((r) => r.file!);

    this.submitting.set(true);
    this.deckService
      .createPages(this.groupKey, specs, files, { overwrite: this.overwrite })
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          this.warnings.set(res.warnings ?? []);
          this.saved.emit();
        },
        error: (err) => {
          this.submitting.set(false);
          this.errorMessage.set(err?.message ?? 'Upload failed.');
          // 409 = a page key (or file name) already exists. Nothing was written.
          this.showOverwriteHint.set(err?.status === 409 && !this.overwrite);
        },
      });
  }

  private submitPatch(): void {
    if (!this.single.title.trim()) return this.errorMessage.set('Title is required.');
    const points = this.toPoints(this.single.points);
    if (!points.length) return this.errorMessage.set('At least one narration point is required.');

    this.submitting.set(true);
    this.deckService
      .patchPage(this.groupKey, this.single.page_key, {
        title: this.single.title.trim(),
        // Always sent: '' explicitly CLEARS the page's own line (group fallback).
        when_to_show: this.single.when.trim(),
        narration_points: points,
        position: Number(this.single.position) || 1,
      })
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.saved.emit();
        },
        error: (err) => {
          this.submitting.set(false);
          this.errorMessage.set(err?.message ?? 'Save failed.');
        },
      });
  }

  private submitReplaceHtml(): void {
    if (!this.single.file) return this.errorMessage.set('Choose the replacement HTML file.');

    // A deliberate overwrite of one existing page: same key, new HTML, version bumps.
    const spec: PageSpec = {
      page_key: this.single.page_key,
      title: this.single.title,
      narration_points: this.toPoints(this.single.points),
      position: Number(this.single.position) || 1,
      file_name: this.slide?.file,
      is_active: this.slide?.isActive ?? true,
    };

    this.submitting.set(true);
    this.deckService
      .createPages(this.groupKey, [spec], [this.single.file], { overwrite: true })
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          this.warnings.set(res.warnings ?? []);
          this.saved.emit();
        },
        error: (err) => {
          this.submitting.set(false);
          this.errorMessage.set(err?.message ?? 'Replace failed.');
        },
      });
  }

  onSingleFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.single.file = input.files?.[0] ?? null;
  }

  onClose(): void {
    this.closed.emit();
  }
}
